/**
 * usePermissions - single source of truth for permission checks in the UI.
 *
 * Why this exists: the JWT embeds a `permissions` claim, but that is only a
 * snapshot taken at login. Since roles became granular (role_presets plus
 * per-user module overrides) the API resolves permissions from the database on
 * every request, so the claim can be arbitrarily stale. Anything the UI gates
 * must therefore come from get_my_permissions.php, not from the token.
 *
 * The resolved permissions are cached in module scope for the lifetime of the
 * page so that several components mounting at once cause a single request.
 * Call resetPermissions() after an action that changes your own permissions.
 */
import { useCallback, useEffect, useState } from 'react';
import AccountsUsersService from '../services/AccountsUsersService';

let cache = null;
let inFlight = null;

export function resetPermissions() {
    cache = null;
    inFlight = null;
}

async function load() {
    if (cache) return cache;
    if (!inFlight) {
        inFlight = AccountsUsersService.getMyPermissions()
            .then((data) => {
                cache = data;
                return data;
            })
            .catch((e) => {
                // A failed load is not cached: leaving the UI permanently locked
                // out because of a transient error would be worse than retrying.
                inFlight = null;
                throw e;
            });
    }
    return inFlight;
}

export function usePermissions() {
    const [permissions, setPermissions] = useState(cache);
    const [loading, setLoading] = useState(!cache);
    const [error, setError] = useState('');

    const refresh = useCallback(async () => {
        resetPermissions();
        setLoading(true);
        setError('');
        try {
            const data = await load();
            setPermissions(data);
            return data;
        } catch (e) {
            setError(e.message || 'No se pudieron cargar los permisos');
            setPermissions(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let alive = true;
        if (cache) {
            setPermissions(cache);
            setLoading(false);
            return undefined;
        }
        load()
            .then((data) => {
                if (alive) {
                    setPermissions(data);
                    setLoading(false);
                }
            })
            .catch((e) => {
                if (alive) {
                    setError(e.message || 'No se pudieron cargar los permisos');
                    setLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, []);

    /**
     * can('users', 'assign_role') -> boolean
     *
     * Fails closed: while loading, on error, or with no permissions, every
     * check returns false. The server enforces the same rule independently, so
     * this only ever hides UI the user could not have used anyway.
     */
    const can = useCallback((module, action) => {
        if (!permissions || !permissions.modules) return false;
        const actions = permissions.modules[module];
        if (!actions) return false;
        return permTruthy(actions[action]);
    }, [permissions]);

    return {
        permissions,
        loading,
        error,
        can,
        refresh,
        // Handy for picking a tab default without hardcoding a role code.
        role: permissions ? permissions.role : null,
        internal: permissions ? Boolean(permissions.internal) : false,
    };
}

// The API normalises grants to 1/0, but tolerate the shapes a stale token or a
// hand-edited row could carry rather than treating "truthy string" as truthy.
function permTruthy(value) {
    return value === 1 || value === true || value === '1';
}

export default usePermissions;
